import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Modal,
} from 'react-native';
import { favoritesService, FavoriteBusiness } from '../services/favoritesService';
import { Business } from '../types/business';
import UserBottomMenu from '../components/UserBottomMenu';
import AppointmentRequestForm from '../components/AppointmentRequestForm';
import TransportationQuoteRequestForm from '../components/TransportationQuoteRequestForm';
import { isTransportationBusinessCategory } from '../utils/transportationCategoryUtils';

interface UserFavoritesScreenProps {
  onExploreBusinesses?: () => void;
  onBookBusiness?: (business: Business) => void;
  onTabPress?: (tab: 'dashboard' | 'appointments' | 'favorites' | 'profile' | 'business-exploration' | 'work') => void;
  onLogout?: () => void;
}

const UserFavoritesScreen: React.FC<UserFavoritesScreenProps> = ({
  onExploreBusinesses,
  onBookBusiness,
  onTabPress,
  onLogout,
}) => {
  const [favoriteBusinesses, setFavoriteBusinesses] = useState<FavoriteBusiness[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [selectedBusinessForRequest, setSelectedBusinessForRequest] = useState<Business | null>(null);
  const [requestType, setRequestType] = useState<'appointment' | 'estimate'>('appointment');

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      setLoading(true);
      const favorites = await favoritesService.getFavoriteBusinesses();
      setFavoriteBusinesses(favorites);
    } catch (error) {
      console.error('Error loading favorites:', error);
      Alert.alert('Error', 'Failed to load favorite businesses');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (businessId: string) => {
    try {
      const success = await favoritesService.removeFavorite(businessId);
      if (success) {
        await loadFavorites(); // Reload the list
        Alert.alert('Removed', 'Business removed from favorites');
      } else {
        Alert.alert('Error', 'Failed to remove business from favorites');
      }
    } catch (error) {
      console.error('Error removing favorite:', error);
      Alert.alert('Error', 'Failed to remove business from favorites');
    }
  };

  const handleBookBusiness = (business: FavoriteBusiness) => {
    if (business.isOnBookAPT === true) {
      if (onBookBusiness) {
        onBookBusiness(business as unknown as Business);
      } else {
        console.warn('❌ onBookBusiness callback is null/undefined');
      }
    } else {
      Alert.alert('Instant Booking Unavailable', `${business.name} is not on BookAPT yet. Use Request Booking or Request Estimate.`);
    }
  };

  const handleOpenRequestForm = (business: FavoriteBusiness, type: 'appointment' | 'estimate') => {
    setRequestType(type);
    setSelectedBusinessForRequest(business as unknown as Business);
    setShowRequestForm(true);
  };

  const handleRequestFormSubmitted = () => {
    setShowRequestForm(false);
    setSelectedBusinessForRequest(null);
    Alert.alert('Success', 'Your appointment request has been sent!');
  };

  const handleRequestFormCancel = () => {
    setShowRequestForm(false);
    setSelectedBusinessForRequest(null);
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.loadingText}>Loading favorites...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.placeholder} />
        <Text style={styles.headerTitle}>Your Favorites</Text>
        <View style={styles.placeholder} />
      </View>

      {/* Content */}
      <ScrollView 
        style={styles.content} 
        contentContainerStyle={styles.scrollContent}
        scrollEnabled={true}
        showsVerticalScrollIndicator={true}
      >
        {/* Explore Businesses Button */}
        <TouchableOpacity
          style={styles.exploreButton}
          onPress={onExploreBusinesses}
        >
          <Text style={styles.exploreButtonIcon}>🔍</Text>
          <View style={styles.exploreButtonContent}>
            <Text style={styles.exploreButtonTitle}>Explore Businesses</Text>
            <Text style={styles.exploreButtonSubtitle}>Discover new businesses nearby</Text>
          </View>
          <Text style={styles.exploreButtonArrow}>›</Text>
        </TouchableOpacity>

        {/* Favorites List */}
        <View style={styles.favoritesSection}>
          <Text style={styles.sectionTitle}>
            ❤️ Favorite Businesses ({favoriteBusinesses.length})
          </Text>
          
          {favoriteBusinesses.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateIcon}>💔</Text>
              <Text style={styles.emptyStateTitle}>No favorites yet</Text>
              <Text style={styles.emptyStateSubtitle}>
                Start exploring businesses and add them to your favorites!
              </Text>
              <TouchableOpacity
                style={styles.exploreEmptyButton}
                onPress={onExploreBusinesses}
              >
                <Text style={styles.exploreEmptyButtonText}>Explore Now</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.businessList}>
              {favoriteBusinesses.map((business) => {
                const CardWrapper = View; // Card itself is not clickable, only buttons are
                return (
                  <CardWrapper
                    key={business.id}
                    style={styles.businessCard}
                  >
                    <View style={styles.businessInfo}>
                      <Text style={styles.businessName} numberOfLines={2} ellipsizeMode="tail">{business.name}</Text>
                      <Text style={styles.businessCategory}>{business.category}</Text>
                      <View style={styles.businessRating}>
                        <Text style={styles.ratingText}>⭐ {business.rating}</Text>
                      </View>
                      <View style={styles.businessActions} pointerEvents="box-none">
                        {business.isOnBookAPT === true ? (
                          <TouchableOpacity
                            style={styles.cardActionBtn}
                            activeOpacity={0.8}
                            accessibilityLabel={`Book with ${business.name}`}
                            accessibilityRole="button"
                            onPress={() => handleBookBusiness(business)}
                          >
                            <Text style={styles.cardActionBtnText} numberOfLines={2}>
                              Book
                            </Text>
                          </TouchableOpacity>
                        ) : (
                          <TouchableOpacity
                            style={styles.cardActionBtn}
                            activeOpacity={0.8}
                            accessibilityLabel={`Request booking with ${business.name}`}
                            accessibilityRole="button"
                            onPress={() => handleOpenRequestForm(business, 'appointment')}
                          >
                            <Text style={styles.cardActionBtnText} numberOfLines={2}>
                              Request Booking
                            </Text>
                          </TouchableOpacity>
                        )}
                        <TouchableOpacity
                          style={styles.cardActionBtn}
                          activeOpacity={0.8}
                          accessibilityLabel={`${business.isOnBookAPT === true ? 'Request estimate from' : 'Request estimate from'} ${business.name}`}
                          accessibilityRole="button"
                          onPress={() => handleOpenRequestForm(business, 'estimate')}
                        >
                          <Text style={styles.cardActionBtnText} numberOfLines={2}>
                            {business.isOnBookAPT === true ? 'Estimate' : 'Request Estimate'}
                          </Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                    <View style={styles.favoriteActions} pointerEvents="box-none">
                      <TouchableOpacity
                        style={styles.favoriteIconButton}
                        activeOpacity={0.7}
                        accessibilityLabel={`Remove ${business.name} from favorites`}
                        accessibilityRole="button"
                        accessibilityHint="Removes this business from your favorites list"
                        onPress={() => handleRemoveFavorite(business.id)}
                      >
                        <Text style={styles.favoriteIconButtonText}>❤️</Text>
                      </TouchableOpacity>
                    </View>
                  </CardWrapper>
                );
              })}
            </View>
          )}
        </View>
      </ScrollView>

      {/* Request Appointment form for favorites not on BookAPT (middle-man flow) */}
      <Modal
        visible={showRequestForm && selectedBusinessForRequest !== null}
        animationType="slide"
        transparent
        onRequestClose={handleRequestFormCancel}
        statusBarTranslucent
      >
        <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={[StyleSheet.absoluteFill, styles.modalBackdrop]}
            activeOpacity={1}
            onPress={handleRequestFormCancel}
          >
            <View
              style={styles.modalContent}
              onStartShouldSetResponder={() => true}
            >
              {selectedBusinessForRequest ? (
                requestType === 'estimate' &&
                isTransportationBusinessCategory(selectedBusinessForRequest.category) ? (
                  <TransportationQuoteRequestForm
                    business={selectedBusinessForRequest}
                    onCancel={handleRequestFormCancel}
                    onSubmit={handleRequestFormSubmitted}
                  />
                ) : (
                  <AppointmentRequestForm
                    business={selectedBusinessForRequest}
                    requestType={requestType}
                    onCancel={handleRequestFormCancel}
                    onSubmit={handleRequestFormSubmitted}
                  />
                )
              ) : null}
            </View>
          </TouchableOpacity>
        </View>
      </Modal>
      
      {/* Bottom Menu */}
      <UserBottomMenu
        activeTab="favorites"
        onTabPress={onTabPress}
        onLogout={onLogout}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 60,
    paddingBottom: 20,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  placeholder: {
    width: 60,
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingBottom: 70, // Space for bottom menu (consistent across all screens)
    flexGrow: 1,
    minHeight: '100%', // Ensure content takes full height even when empty
  },
  exploreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 16,
    marginTop: 20,
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  exploreButtonIcon: {
    fontSize: 24,
    marginRight: 16,
  },
  exploreButtonContent: {
    flex: 1,
  },
  exploreButtonTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  exploreButtonSubtitle: {
    fontSize: 14,
    color: '#666',
  },
  exploreButtonArrow: {
    fontSize: 20,
    color: '#007AFF',
    fontWeight: 'bold',
  },
  favoritesSection: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 20,
  },
  emptyStateIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  emptyStateSubtitle: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 22,
  },
  exploreEmptyButton: {
    backgroundColor: '#2563EB', // Professional blue
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  exploreEmptyButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  businessList: {
    gap: 12,
  },
  businessCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: 'white',
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  businessInfo: {
    flex: 1,
    minWidth: 0,
  },
  businessName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  businessCategory: {
    fontSize: 14,
    color: '#007AFF',
    marginBottom: 4,
  },
  businessRating: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  ratingText: {
    fontSize: 12,
    color: '#333',
  },
  businessActions: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 8,
    marginTop: 10,
  },
  cardActionBtn: {
    flex: 1,
    minHeight: 34,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#2563EB',
    backgroundColor: '#EFF6FF',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
    paddingVertical: 6,
  },
  cardActionBtnText: {
    color: '#1E3A8A',
    fontSize: 11,
    fontWeight: '700',
    textAlign: 'center',
    lineHeight: 14,
  },
  favoriteActions: {
    marginLeft: 10,
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8,
    paddingTop: 2,
  },
  favoriteIconButton: {
    backgroundColor: '#F3F4F6',
    width: 30,
    height: 30,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  favoriteIconButtonText: {
    fontSize: 13,
    color: '#6B7280',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalBackdrop: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 12,
    width: '100%',
    maxWidth: 500,
    maxHeight: '90%',
    minHeight: 400,
    overflow: 'hidden',
  },
});

export default UserFavoritesScreen;